import React from "react";
import { ClipboardPaste, ImagePlus, Trash2 } from "lucide-react";
import { transparentBillingLogo } from "../../utils/billingLogoImage.js";

const BUTTON = "inline-flex min-h-7 items-center gap-1.5 rounded-[4px] border px-2.5 py-1 text-xs font-bold transition";
const PICK_BUTTON = `${BUTTON} cursor-pointer border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-accent has-[:disabled]:cursor-not-allowed has-[:disabled]:border-slate-200 has-[:disabled]:bg-slate-50 has-[:disabled]:text-slate-400`;
const REMOVE_BUTTON = `${BUTTON} border-red-300 bg-white text-red-700 hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400`;
const SECONDARY_BUTTON = `${BUTTON} border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400`;
const APPLY_BUTTON = `${BUTTON} border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400`;

/** Logo affiché sur les documents : aperçu, ajout par Ctrl+V ou par fichier, retrait. Le serveur vérifie et réduit l'image. */
export default function BillingLogoField({ title, logoUrl, note, editable, disabled = false, onUpload, onRemove }) {
  const [error, setError] = React.useState("");
  const [working, setWorking] = React.useState(false);
  const [transparentLogo, setTransparentLogo] = React.useState(null);
  const zoneRef = React.useRef(null);
  const workingRef = React.useRef(false);
  const blocked = disabled || working;
  React.useEffect(() => { setTransparentLogo(null); }, [logoUrl]);
  React.useEffect(() => () => { if (transparentLogo) URL.revokeObjectURL(transparentLogo.url); }, [transparentLogo]);
  const run = async (action) => {
    if (disabled || workingRef.current) return;
    workingRef.current = true;
    setWorking(true);
    setError("");
    try { await action(); } catch (failure) {
      if (failure.name === "NotAllowedError" || failure.name === "SecurityError") {
        zoneRef.current?.focus();
        setError("Cliquez dans le cadre puis appuyez sur Ctrl+V pour coller l’image copiée.");
      } else setError(failure.message || "Le logo n’a pas été enregistré.");
    } finally { workingRef.current = false; setWorking(false); }
  };
  const paste = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (blocked || transparentLogo) return;
    const file = [...(event.clipboardData?.files || [])].find((entry) => entry.type.startsWith("image/"))
      || [...(event.clipboardData?.items || [])].find((entry) => entry.kind === "file" && entry.type.startsWith("image/"))?.getAsFile();
    if (file) void run(() => onUpload(file));
    else setError("Copiez l’image du logo elle-même, puis collez-la ici avec Ctrl+V.");
  };
  const readClipboard = () => {
    if (!navigator.clipboard?.read) {
      zoneRef.current?.focus();
      setError("Appuyez sur Ctrl+V dans le cadre pour coller l’image copiée.");
      return;
    }
    void run(async () => {
      const entries = await navigator.clipboard.read();
      for (const entry of entries) {
        const type = entry.types.find((value) => ["image/png", "image/jpeg", "image/webp"].includes(value));
        if (type) {
          await onUpload(new File([await entry.getType(type)], "logo-colle", { type }));
          return;
        }
      }
      throw new Error("Le presse-papiers ne contient pas d’image. Copiez l’image du logo elle-même, puis réessayez.");
    });
  };
  return <section className="rounded-[4px] border border-slate-200 p-2.5" onPaste={editable ? paste : undefined}
    onClick={editable ? (event) => { if (!event.target.closest("button, input, label")) zoneRef.current?.focus(); } : undefined}>
    <h3 className="text-xs font-extrabold uppercase tracking-wide text-slate-500">{title}</h3>
    <div className="mt-1.5 flex flex-wrap items-center gap-2.5">
      <div ref={zoneRef} role="group" aria-label={editable ? `${title} : cliquez ici puis Ctrl+V pour coller un logo` : title}
        tabIndex={editable ? 0 : undefined} aria-disabled={editable ? blocked || Boolean(transparentLogo) : undefined}
        style={{ backgroundImage: "conic-gradient(#e2e8f0 25%, #fff 0 50%, #e2e8f0 0 75%, #fff 0)", backgroundSize: "12px 12px" }}
        className="flex h-20 w-44 shrink-0 items-center justify-center rounded-[4px] border border-dashed border-slate-300 bg-slate-50 p-1.5 focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-accent">
        {logoUrl ? <img src={transparentLogo?.url || logoUrl} alt={transparentLogo ? "Aperçu du logo transparent" : "Logo actuel"} className="h-full w-full object-contain" /> : <span className="bg-slate-50 px-1 text-xs text-slate-500">Aucun logo</span>}
      </div>
      <div className="min-w-0 flex-1 basis-40 space-y-1.5">
        {editable ? transparentLogo ? <div className="flex flex-wrap gap-1.5">
          <button type="button" className={APPLY_BUTTON} disabled={blocked} onClick={() => run(async () => {
            await onUpload(transparentLogo.file); setTransparentLogo(null);
          })}>Appliquer la transparence</button>
          <button type="button" className={SECONDARY_BUTTON} disabled={blocked} onClick={() => { setTransparentLogo(null); setError(""); }}>Annuler</button>
        </div> : <div className="flex flex-wrap gap-1.5">
          <button type="button" className={SECONDARY_BUTTON} disabled={blocked} onClick={readClipboard}><ClipboardPaste size={13} aria-hidden="true" />Coller le logo</button>
          <label className={PICK_BUTTON}><ImagePlus size={13} aria-hidden="true" />{logoUrl ? "Remplacer" : "Choisir un fichier"}
            <input className="sr-only" type="file" accept="image/png,image/jpeg,image/webp" disabled={blocked}
              onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; if (file) void run(() => onUpload(file)); }} />
          </label>
          {logoUrl ? <>
            <button type="button" className={SECONDARY_BUTTON} disabled={blocked} onClick={() => run(async () => {
              const file = await transparentBillingLogo(logoUrl);
              setTransparentLogo({ file, url: URL.createObjectURL(file) });
            })}>Rendre le fond blanc transparent</button>
            <button type="button" className={REMOVE_BUTTON} disabled={blocked} onClick={() => run(onRemove)}><Trash2 size={13} aria-hidden="true" />Retirer</button>
          </> : null}
        </div> : null}
        <p className="text-xs text-slate-500">{transparentLogo ? "Aperçu : les zones blanches deviennent transparentes. Vérifiez le résultat puis appliquez ou annulez."
          : <>{editable ? "Cliquez dans cette section puis collez l’image avec Ctrl+V (PNG, JPEG ou WebP). " : ""}{note}</>}</p>
        {working ? <p role="status" className="text-xs text-slate-500">Traitement du logo…</p> : null}
        {error ? <p role="alert" className="text-xs font-bold text-red-700">{error}</p> : null}
      </div>
    </div>
  </section>;
}
